/**
 * Centralized Step Validation Service
 * Single source of truth for all step validation logic
 */

import type { ParsedField } from '../utils/templateParsers';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

class StepValidationService {
  /**
   * Validate General step
   */
  static async validateGeneral(
    formData: Record<string, unknown>,
    validateForm: (
      fields: ParsedField[],
      data: Record<string, { value: string; hasError: boolean }>
    ) => Promise<boolean>
  ): Promise<ValidationResult> {
    const requiredFields: ParsedField[] = [
      {
        type: 'TextField',
        label: 'Application Name',
        attributeName: 'securitysystems.systemname',
        attributeKey: '1',
        required: true,
        defaultValue: '',
        placeholder: 'Enter application name',
      },
    ];

    // Convert formData to validation format
    const validationFormData = Object.entries(formData).reduce<
      Record<string, { value: string; hasError: boolean }>
    >((acc, [key, value]) => {
      acc[key] = { value: String(value || ''), hasError: false };

      return acc;
    }, {});

    const isValid = await validateForm(requiredFields, validationFormData);

    return {
      isValid,
      errors: isValid ? [] : ['Please fill in all required fields'],
    };
  }

  /**
   * Validate attribute mapping steps (CreateAccount, UpdateAccount, etc.)
   */
  static validateAttributeMapping(
    formData: Record<string, unknown>,
    stepName: string
  ): ValidationResult {
    const mappings =
      (formData.mappings as {
        saviyntField?: string;
        externalAppField?: string;
      }[]) || [];

    const isValid =
      mappings.length > 0 &&
      mappings.every(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

    let errors: string[] = [];

    if (!isValid) {
      const filteredMappings = mappings.filter(
        (mapping) => mapping.saviyntField && mapping.externalAppField
      );

      if (mappings.length > 0 && filteredMappings.length > 0) {
        errors = ['Mappings are not correct'];
      } else {
        errors = [
          `At least one attribute mapping is required for ${stepName.toLowerCase()}.`,
        ];
      }
    }

    return {
      isValid,
      errors,
    };
  }

  /**
   * Main validation dispatcher
   */
  static async validateStep(
    stepName: string,
    formData: Record<string, unknown>,
    validateForm?: (
      fields: ParsedField[],
      data: Record<string, { value: string; hasError: boolean }>
    ) => Promise<boolean>
  ): Promise<ValidationResult> {
    switch (stepName) {
      case 'General':
        if (!validateForm) {
          throw new Error('validateForm function required for General step');
        }

        return this.validateGeneral(formData, validateForm);

      case 'CreateAccount':
      case 'UpdateAccount':
      case 'EnableAccount':
      case 'DisableAccount':
      case 'RemoveAccount':
      case 'AddAccess':
      case 'RemoveAccess':
        return this.validateAttributeMapping(formData, stepName);

      default:
        return { isValid: true, errors: [] };
    }
  }
}

export default StepValidationService;
